import React, { useState, useEffect } from 'react';
import { getBackups, createBackup, restoreBackup, deleteBackup, getBackupSettings, updateBackupSettings, getBackupHealth, exportBackupFile, restoreFromFile, downloadBackup, getFTPSettings, saveFTPSettings, testFTPConnection, createMongodumpBackup, getLocalBackups, getRemoteBackups, restoreFTPBackup, restoreRemoteBackup, deleteFTPBackup, getFTPSchedule, updateFTPSchedule } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { Database, Loader2, Plus, Shield, Lock, AlertTriangle, CheckCircle2, XCircle, Clock } from 'lucide-react';

import { PasswordGate } from '../components/backup/PasswordGate';
import { FileExportRestore } from '../components/backup/FileExportRestore';
import { FTPBackupSection } from '../components/backup/FTPBackupSection';
import { BackupSettingsForm } from '../components/backup/BackupSettingsForm';
import { BackupsList } from '../components/backup/BackupsList';
import { BackupDialogs } from '../components/backup/BackupDialogs';
import { SecuritySettings } from '../components/backup/SecuritySettings';
import { RestoreWizard } from '../components/backup/RestoreWizard';

import { authenticateGate } from '../lib/accessGate';

const getErrorMsg = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0 && detail[0]?.msg) return detail[0].msg;
  return fallback;
};

export default function BackupSettings() {
  const [accessGranted, setAccessGranted] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(true);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [settings, setSettings] = useState({
    auto_backup_enabled: true, backup_time: '02:00', retention_days: 2555, max_backups: 100
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [restoreWizardOpen, setRestoreWizardOpen] = useState(false);
  const [backupName, setBackupName] = useState('');
  const [backupDescription, setBackupDescription] = useState('');
  const [exporting, setExporting] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoringFile, setRestoringFile] = useState(false);
  const [restoreFileDialogOpen, setRestoreFileDialogOpen] = useState(false);

  const [ftpSettings, setFtpSettings] = useState({ host: '', port: 21, username: '', password: '', remote_path: '/backups', use_tls: false, use_sftp: false });
  const [ftpTesting, setFtpTesting] = useState(false);
  const [ftpTestResult, setFtpTestResult] = useState(null);
  const [ftpSaving, setFtpSaving] = useState(false);
  const [ftpBackups, setFtpBackups] = useState([]);
  const [remoteBackups, setRemoteBackups] = useState([]);
  const [creatingDump, setCreatingDump] = useState(false);
  const [scanningRemote, setScanningRemote] = useState(false);
  const [restoringFtp, setRestoringFtp] = useState(null);
  const [restoreRemoteConfirm, setRestoreRemoteConfirm] = useState(null);
  const [restoreLocalConfirm, setRestoreLocalConfirm] = useState(null);
  const [remoteError, setRemoteError] = useState(null);
  const [ftpSchedule, setFtpSchedule] = useState({ ftp_schedule_enabled: false, ftp_schedule_interval_hours: 12, ftp_max_remote_backups: 2000 });
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [health, setHealth] = useState(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user.role === 'super_admin';

  useEffect(() => { if (accessGranted) loadData(); }, [accessGranted]);

  const handlePasswordSubmit = async () => {
    setPasswordError('');
    try {
      await authenticateGate(passwordInput);
      setAccessGranted(true);
      setPasswordDialogOpen(false);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        setPasswordError('Incorrect password');
      } else if (status === 503) {
        setPasswordError('Access gate not configured');
      } else {
        setPasswordError('Network error. Try again.');
      }
    }
  };

  const handlePasswordCancel = () => { setPasswordDialogOpen(false); window.history.back(); };

  const loadData = async () => {
    try {
      const [backupsRes, settingsRes, healthRes] = await Promise.all([
        getBackups(), getBackupSettings(), getBackupHealth().catch(() => ({ data: null }))
      ]);
      setBackups(backupsRes.data || []);
      setSettings(settingsRes.data || settings);
      if (healthRes.data) setHealth(healthRes.data);
      loadFTPData();
    } catch (error) {
      toast.error('Failed to load backup data');
    } finally {
      setLoading(false);
    }
  };

  const loadFTPData = async () => {
    try {
      const [ftpRes, localRes, schedRes] = await Promise.all([
        getFTPSettings().catch(() => ({ data: null })),
        getLocalBackups().catch(() => ({ data: [] })),
        getFTPSchedule().catch(() => ({ data: null })),
      ]);
      if (ftpRes.data) setFtpSettings(prev => ({ ...prev, ...ftpRes.data, password: ftpRes.data.password === '••••••••' ? prev.password : ftpRes.data.password }));
      setFtpBackups(localRes.data || []);
      if (schedRes.data) setFtpSchedule(prev => ({ ...prev, ...schedRes.data }));
    } catch (e) { console.error('FTP data load error', e); }
  };

  const handleTestFTP = async () => {
    setFtpTesting(true); setFtpTestResult(null);
    try { setFtpTestResult((await testFTPConnection(ftpSettings)).data); }
    catch (e) { setFtpTestResult({ success: false, message: getErrorMsg(e, 'Test failed') }); }
    finally { setFtpTesting(false); }
  };

  const handleSaveFTP = async () => {
    setFtpSaving(true);
    try { await saveFTPSettings(ftpSettings); toast.success('FTP settings saved'); setFtpTestResult(null); }
    catch (e) { toast.error(getErrorMsg(e, 'Failed to save')); }
    finally { setFtpSaving(false); }
  };

  const handleCreateDump = async () => {
    setCreatingDump(true);
    try {
      const res = await createMongodumpBackup();
      const d = res.data;
      if (d.ftp_uploaded) {
        toast.success('Backup created & uploaded to FTP');
      } else if (d.ftp_error) {
        toast.warning(`Backup created locally but FTP upload failed: ${d.ftp_error}`);
      } else {
        toast.success('Backup created locally');
      }
      loadFTPData();
      // Refresh health status
      getBackupHealth().then(r => { if (r.data) setHealth(r.data); }).catch(() => {});
    } catch (e) { toast.error(getErrorMsg(e, 'Backup failed')); }
    finally { setCreatingDump(false); }
  };

  const handleScanRemote = async () => {
    setScanningRemote(true); setRemoteError(null);
    try { setRemoteBackups((await getRemoteBackups()).data.files || []); }
    catch (e) { const msg = getErrorMsg(e, 'Failed to scan FTP'); setRemoteError(msg); toast.error(msg); }
    finally { setScanningRemote(false); }
  };

  const handleRestoreFTPLocal = async (backup) => {
    setRestoringFtp(backup.id);
    try { await restoreFTPBackup(backup.id); toast.success('Database restored from local backup'); setRestoreLocalConfirm(null); }
    catch (e) { toast.error(getErrorMsg(e, 'Restore failed')); }
    finally { setRestoringFtp(null); }
  };

  const handleRestoreRemote = async (file) => {
    setRestoringFtp(file.path);
    try { await restoreRemoteBackup(file.path); toast.success('Database restored from remote backup'); setRestoreRemoteConfirm(null); }
    catch (e) { toast.error(getErrorMsg(e, 'Remote restore failed')); }
    finally { setRestoringFtp(null); }
  };

  const handleDeleteFTPBackup = async (id) => {
    try { await deleteFTPBackup(id); toast.success('Backup deleted'); loadFTPData(); }
    catch (e) { toast.error('Failed to delete backup'); }
  };

  const handleSaveSchedule = async () => {
    setSavingSchedule(true);
    try { await updateFTPSchedule(ftpSchedule); toast.success('FTP backup schedule saved'); }
    catch (e) { toast.error(getErrorMsg(e, 'Failed to save schedule')); }
    finally { setSavingSchedule(false); }
  };

  const handleCreateBackup = async () => {
    if (!isSuperAdmin) { toast.error('Only super admins can create backups'); return; }
    setCreating(true);
    try {
      await createBackup({ name: backupName || `Manual Backup ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, description: backupDescription || null });
      toast.success('Backup created successfully'); setCreateDialogOpen(false); setBackupName(''); setBackupDescription(''); loadData();
    } catch (error) { toast.error(getErrorMsg(error, 'Failed to create backup')); }
    finally { setCreating(false); }
  };

  const handleRestore = async () => {
    // Handled by RestoreWizard now
  };

  const handleDelete = async () => {
    if (!isSuperAdmin || !selectedBackup) return;
    try { await deleteBackup(selectedBackup.id); toast.success('Backup deleted'); setDeleteDialogOpen(false); setSelectedBackup(null); loadData(); }
    catch (error) { toast.error('Failed to delete backup'); }
  };

  const handleSaveSettings = async () => {
    if (!isSuperAdmin) return;
    setSavingSettings(true);
    try { await updateBackupSettings(settings); toast.success('Backup settings saved'); }
    catch (error) { toast.error('Failed to save settings'); }
    finally { setSavingSettings(false); }
  };

  const handleExportFile = async () => {
    setExporting(true);
    try {
      const response = await exportBackupFile();
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `cashelblue_backup_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); a.remove();
      toast.success('Backup exported successfully');
    } catch (error) { toast.error(getErrorMsg(error, 'Failed to export backup')); }
    finally { setExporting(false); }
  };

  const handleDownloadBackup = async (backup) => {
    try {
      const response = await downloadBackup(backup.id);
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `cashelblue_backup_${backup.id.slice(0, 8)}.json`;
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); a.remove();
      toast.success('Backup downloaded');
    } catch (error) { toast.error('Failed to download backup'); }
  };

  const handleRestoreFromFile = async () => {
    if (!restoreFile) return;
    setRestoringFile(true);
    try {
      const data = (await restoreFromFile(restoreFile)).data;
      toast.success(`Restored from file! ${Object.values(data.restored_counts || {}).reduce((a, b) => a + b, 0)} records.`);
      setRestoreFileDialogOpen(false); setRestoreFile(null); loadData();
    } catch (error) { toast.error(getErrorMsg(error, 'Failed to restore from file')); }
    finally { setRestoringFile(false); }
  };

  const formatBytes = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  if (!accessGranted) {
    return (
      <PasswordGate
        passwordDialogOpen={passwordDialogOpen}
        passwordInput={passwordInput}
        setPasswordInput={setPasswordInput}
        passwordError={passwordError}
        onSubmit={handlePasswordSubmit}
        onCancel={handlePasswordCancel}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="backup-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium flex items-center gap-2">
            <Database className="w-5 h-5" />
            Data Management & Security
          </h2>
          <p className="text-sm text-muted-foreground">Manage backups, restore data, and configure security settings</p>
        </div>
      </div>

      <Tabs defaultValue="database" className="w-full">
        <TabsList data-testid="data-management-tabs">
          <TabsTrigger value="database" data-testid="tab-database">
            <Database className="w-4 h-4 mr-2" /> Database
          </TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">
            <Lock className="w-4 h-4 mr-2" /> Security
          </TabsTrigger>
        </TabsList>

        <TabsContent value="database" className="space-y-6 mt-4">
          {/* === BACKUP HEALTH STATUS === */}
          {health && (
            <Card className={health.issues?.length > 0 ? 'border-amber-300 bg-amber-50/50' : 'border-emerald-200 bg-emerald-50/30'} data-testid="backup-health-card">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  {health.issues?.length > 0 ? (
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                  ) : (
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                  )}
                  <div className="flex-1 space-y-3">
                    <h3 className="text-sm font-semibold">
                      {health.issues?.length > 0 ? 'Backup System, Attention Required' : 'Backup System, Healthy'}
                    </h3>
                    
                    {/* Issues */}
                    {health.issues?.length > 0 && (
                      <div className="space-y-1">
                        {health.issues.map((issue, i) => (
                          <div key={i} className="flex items-start gap-2 text-xs text-amber-800">
                            <XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            <span>{issue}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Status Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 text-xs">
                      {/* DB Backups */}
                      <div className="space-y-1">
                        <div className="font-medium text-muted-foreground uppercase tracking-wide">Database Backups</div>
                        <div>{health.db_backups?.total || 0} total backups</div>
                        {health.db_backups?.last_backup?.date ? (
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Last: {formatDistanceToNow(new Date(health.db_backups.last_backup.date), { addSuffix: true })}
                            {health.db_backups.last_backup.is_auto && <span className="text-blue-600">(auto)</span>}
                          </div>
                        ) : (
                          <div className="text-amber-700">No backups yet</div>
                        )}
                      </div>

                      {/* Local .gz Backups */}
                      <div className="space-y-1">
                        <div className="font-medium text-muted-foreground uppercase tracking-wide">Local Files (.gz)</div>
                        <div>{health.local_backups?.total || 0} local backups</div>
                        {health.local_backups?.last_backup_date ? (
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Last: {formatDistanceToNow(new Date(health.local_backups.last_backup_date), { addSuffix: true })}
                          </div>
                        ) : (
                          <div className="text-amber-700">No local files yet</div>
                        )}
                      </div>

                      {/* Auto Scheduler */}
                      <div className="space-y-1">
                        <div className="font-medium text-muted-foreground uppercase tracking-wide">Auto Scheduler</div>
                        <div className="flex items-center gap-1.5">
                          <span className={`w-2 h-2 rounded-full ${health.auto_scheduler?.enabled ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                          {health.auto_scheduler?.enabled ? `Enabled (${health.auto_scheduler.backup_time_utc} UTC)` : 'Disabled'}
                        </div>
                        {health.auto_scheduler?.last_run_date && (
                          <div>Last run: {health.auto_scheduler.last_run_date}</div>
                        )}
                      </div>

                      {/* FTP Scheduler */}
                      <div className="space-y-1">
                        <div className="font-medium text-muted-foreground uppercase tracking-wide">FTP Scheduler</div>
                        <div className="flex items-center gap-1.5">
                          <span className={`w-2 h-2 rounded-full ${health.ftp_scheduler?.enabled ? (health.ftp_scheduler.has_valid_password ? 'bg-emerald-500' : 'bg-red-500') : 'bg-gray-300'}`} />
                          {health.ftp_scheduler?.enabled ? (
                            health.ftp_scheduler.has_valid_password ? 
                              `Every ${health.ftp_scheduler.interval_hours}h` : 
                              <span className="text-red-600 font-medium">Password missing!</span>
                          ) : 'Disabled'}
                        </div>
                        {health.ftp_scheduler?.last_ftp_uploaded_date && (
                          <div>Last upload: {formatDistanceToNow(new Date(health.ftp_scheduler.last_ftp_uploaded_date), { addSuffix: true })}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex justify-end">
            {isSuperAdmin && (
              <Button onClick={() => setCreateDialogOpen(true)} data-testid="btn-create-backup">
                <Plus className="w-4 h-4 mr-2" /> Create Backup
              </Button>
            )}
          </div>

      {!isSuperAdmin && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex items-center gap-3">
            <Shield className="w-5 h-5 text-amber-600" />
            <p className="text-sm text-amber-800">Only Super Admins can create, restore, or delete backups.</p>
          </CardContent>
        </Card>
      )}

      {isSuperAdmin && (
        <FileExportRestore
          exporting={exporting}
          onExport={handleExportFile}
          restoreFile={restoreFile}
          onFileChange={(e) => {
            const f = e.target.files?.[0] || null;
            setRestoreFile(f);
          }}
          onRestoreClick={() => {
            if (restoreFile) {
              setSelectedBackup(null);
              setRestoreWizardOpen(true);
            }
          }}
          formatBytes={formatBytes}
        />
      )}

      {isSuperAdmin && (
        <FTPBackupSection
          ftpSettings={ftpSettings}
          setFtpSettings={setFtpSettings}
          ftpTesting={ftpTesting}
          ftpTestResult={ftpTestResult}
          ftpSaving={ftpSaving}
          ftpBackups={ftpBackups}
          remoteBackups={remoteBackups}
          remoteError={remoteError}
          creatingDump={creatingDump}
          scanningRemote={scanningRemote}
          restoringFtp={restoringFtp}
          restoreLocalConfirm={restoreLocalConfirm}
          setRestoreLocalConfirm={setRestoreLocalConfirm}
          restoreRemoteConfirm={restoreRemoteConfirm}
          setRestoreRemoteConfirm={setRestoreRemoteConfirm}
          onTestFTP={handleTestFTP}
          onSaveFTP={handleSaveFTP}
          onCreateDump={handleCreateDump}
          onScanRemote={handleScanRemote}
          onRestoreLocal={handleRestoreFTPLocal}
          onRestoreRemote={handleRestoreRemote}
          onDeleteBackup={handleDeleteFTPBackup}
          ftpSchedule={ftpSchedule}
          setFtpSchedule={setFtpSchedule}
          onSaveSchedule={handleSaveSchedule}
          savingSchedule={savingSchedule}
        />
      )}

      <BackupSettingsForm
        settings={settings}
        setSettings={setSettings}
        isSuperAdmin={isSuperAdmin}
        savingSettings={savingSettings}
        onSave={handleSaveSettings}
      />

      <BackupsList
        backups={backups}
        isSuperAdmin={isSuperAdmin}
        formatBytes={formatBytes}
        onCreateClick={() => setCreateDialogOpen(true)}
        onDownload={handleDownloadBackup}
        onRestore={(backup) => { setSelectedBackup(backup); setRestoreWizardOpen(true); }}
        onDelete={(backup) => { setSelectedBackup(backup); setDeleteDialogOpen(true); }}
      />

      <BackupDialogs
        createDialogOpen={createDialogOpen}
        setCreateDialogOpen={setCreateDialogOpen}
        backupName={backupName}
        setBackupName={setBackupName}
        backupDescription={backupDescription}
        setBackupDescription={setBackupDescription}
        creating={creating}
        onCreateBackup={handleCreateBackup}
        restoreDialogOpen={false}
        setRestoreDialogOpen={() => {}}
        selectedBackup={selectedBackup}
        restoring={restoring}
        onRestore={handleRestore}
        deleteDialogOpen={deleteDialogOpen}
        setDeleteDialogOpen={setDeleteDialogOpen}
        onDelete={handleDelete}
        restoreFileDialogOpen={restoreFileDialogOpen}
        setRestoreFileDialogOpen={setRestoreFileDialogOpen}
        restoreFile={restoreFile}
        restoringFile={restoringFile}
        onRestoreFromFile={handleRestoreFromFile}
        formatBytes={formatBytes}
      />

      {/* Guided Restore Wizard */}
      <RestoreWizard
        open={restoreWizardOpen}
        onOpenChange={(v) => { setRestoreWizardOpen(v); if (!v) setRestoreFile(null); }}
        backup={selectedBackup}
        file={!selectedBackup ? restoreFile : null}
        onRestoreComplete={() => { loadData(); setSelectedBackup(null); setRestoreFile(null); }}
      />
        </TabsContent>

        <TabsContent value="security" className="mt-4">
          <SecuritySettings />
        </TabsContent>
      </Tabs>
    </div>
  );
}
